import { useCallback, useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { useTranslate } from "@similarweb/i18n-provider";
import Sentry from "@similarweb/sentry-logger";

import type { StartupSettingsUser } from "app/@types/startupSettings.types";
import { swSettings } from "app/common/services/swSettings";
import type { FeatureArea } from "app/components/PackageSwitcher/consts/packageFeatureInfo";
import type { PaymentPeriod } from "app/components/PackageSwitcher/consts/paymentPeriod";
import { TrackingAction } from "app/components/PackageSwitcher/consts/TrackingActions";
import { PackagesId } from "app/constants/PackagesId";
import { allTrackers } from "app/services/track/track";

import { fetchSwitchPackagePreview } from "./packageSwitchApiService";

const SOLUTION_WEB = "web-solution";
const SOLUTION_WEB_OLD = "marketing";

const resolveTargetPackageName = (packageId: string): string => {
    const userSolution =
        (swSettings?.user as StartupSettingsUser | undefined)?.customData?.quiz?.UserChosenSolution ?? "";

    if (userSolution !== SOLUTION_WEB && userSolution !== SOLUTION_WEB_OLD) {
        return packageId;
    }

    if (packageId === PackagesId.AEO_CI_SEO) {
        return PackagesId.CI_SEO;
    }

    return packageId;
};

interface ISwitchPackageTarget {
    targetPackageName: string;
    targetPackagePeriod: PaymentPeriod;
}

export const useSwitchPackagePopup = () => {
    const { translate } = useTranslate();
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [target, setTarget] = useState<ISwitchPackageTarget | null>(null);
    const [featureArea, setFeatureArea] = useState<FeatureArea | null>(null);
    const [hookTrackingKey, setHookTrackingKey] = useState<string | null>(null);

    const previewQuery = useQuery({
        queryKey: ["packageSwitcher", "preview", target],
        queryFn: () => {
            if (target === null) {
                throw new Error("target must be set when preview query is enabled");
            }
            return fetchSwitchPackagePreview(target);
        },
        enabled: isPopupOpen && target !== null,
        throwOnError: (error) => {
            Sentry.errorTracker({
                e: error instanceof Error ? error : new Error(String(error)),
                message: "Error fetching switch package preview",
                level: "error",
                params: {
                    extra: {
                        targetPackageName: target?.targetPackageName,
                        targetPackagePeriod: target?.targetPackagePeriod,
                    },
                },
            });
            return false;
        },
    });

    useEffect(() => {
        if (!isPopupOpen) {
            setTarget(null);
            setFeatureArea(null);
            setHookTrackingKey(null);
        }
    }, [isPopupOpen]);

    const triggerPopup = useCallback(
        (packageId: string, paymentPeriod: PaymentPeriod, area: FeatureArea, hookKey: string) => {
            const resolvedTargetPackageName = resolveTargetPackageName(packageId);
            allTrackers.dispatchTrackEvent(
                "one click package",
                TrackingAction.SHOW,
                `${hookKey}+${resolvedTargetPackageName}/one click package modal`,
            );
            setTarget({ targetPackageName: resolvedTargetPackageName, targetPackagePeriod: paymentPeriod });
            setFeatureArea(area);
            setHookTrackingKey(hookKey);
            setIsPopupOpen(true);
        },
        [],
    );

    return {
        isPopupOpen,
        setIsPopupOpen,
        targetPaymentPeriod: target?.targetPackagePeriod ?? null,
        isLoading: previewQuery.isLoading,
        error: previewQuery.isError ? translate("buyer_journey.switch_package.get_switch_options.error") : null,
        switchInformationData: previewQuery.data ?? null,
        availableAddOns: previewQuery.data?.availableAddOns ?? [],
        featureArea,
        hookTrackingKey,
        triggerPopup,
    };
};
